import React from 'react';
import {
  Box,
  Button,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Drawer
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from "@mui/icons-material";

const HistoryPanel = ({
  conversations,
  sessionId,
  startNewChat,
  loadConversation,
  onRename,
  onDelete,
  getTimeAgo,
  mobileOpen,
  onMobileClose,
  visible = true
}) => {
  const [renaming, setRenaming] = React.useState({ open: false, title: "", sessionId: null });
  const [confirmDelete, setConfirmDelete] = React.useState({ open: false, sessionId: null, title: '' });

  const beginRename = (conv) => {
    setRenaming({ open: true, title: conv.title || 'New chat', sessionId: conv.sessionId });
  };

  const applyRename = () => {
    if (onRename) {
      onRename(renaming.sessionId, renaming.title);
    }
    setRenaming({ open: false, title: '', sessionId: null });
  };

  const handleDelete = (sessionIdToDelete) => {
    if (onDelete) {
      onDelete(sessionIdToDelete);
    }
    setConfirmDelete({ open: false, sessionId: null, title: '' });
  };

  const renderConversationItem = (conv) => {
    const title = conv.title || 'New chat';
    const isCurrent = conv.sessionId === sessionId;
    const dateValue = conv.updatedAt || conv.createdAt || new Date();
    const timeAgo = getTimeAgo(dateValue);
    
    return (
      <ListItemButton
        key={conv.sessionId}
        selected={isCurrent}
        onClick={() => {
          loadConversation(conv.sessionId);
          if (onMobileClose) onMobileClose();
        }}
        onDoubleClick={() => beginRename(conv)}
        sx={{
          py: 1,
          px: 2,
          mx: 1,
          my: '1px',
          borderRadius: '7px',
          backgroundColor: isCurrent ? 'var(--color-bg-tertiary)' : 'transparent',
          '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: 'var(--color-bg-tertiary)' },
          '&:hover': { backgroundColor: 'var(--color-neutral-50)' },
          '& .history-action': { opacity: 0, transition: 'opacity 120ms' },
          '&:hover .history-action, &.Mui-selected .history-action, & .history-action:focus-visible': { opacity: 1 }
        }}
      >
        <ListItemText
          primaryTypographyProps={{ 
            variant: 'body2', 
            noWrap: true,
            sx: {
              fontWeight: isCurrent ? 600 : 400,
              color: title === 'Processing...' ? 'var(--color-warning)' : 'inherit',
              fontStyle: title === 'Processing...' ? 'italic' : 'normal'
            }
          }}
          primary={title === 'Processing...' ? 'Processing...' : title}
          secondary={timeAgo}
          secondaryTypographyProps={{
            variant: 'caption',
            color: 'text.secondary'
          }}
        />
        <Tooltip title="Rename" placement="right">
          <IconButton 
            size="small" 
            edge="end" 
            onClick={(e) => { 
              e.stopPropagation(); 
              beginRename(conv); 
            }}
            className="history-action"
            aria-label={`Rename ${title}`}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" placement="right">
          <IconButton 
            size="small" 
            edge="end" 
            onClick={(e) => { 
              e.stopPropagation(); 
              setConfirmDelete({ open: true, sessionId: conv.sessionId, title: conv.title || 'this conversation' }); 
            }}
            className="history-action"
            aria-label={`Delete ${title}`}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </ListItemButton>
    );
  };

  const panelContent = (
    <>
      {/* Header */}
      <Box sx={{ p: 2, pb: 1.5, borderBottom: '1px solid var(--color-border-light)' }}>
        {/* MUI Typography sets its own text color from the theme by default — it does NOT
            reliably inherit the parent Box's `color: white` via plain CSS cascade, so without
            an explicit color here this rendered in the theme's (dark) text-primary instead,
            invisible against this dark header. */}
        <Typography variant="overline" sx={{ display: 'block', color: 'var(--color-text-tertiary)', fontSize: '0.6875rem', lineHeight: 1.6, mb: 1 }}>
          Chat History
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          fullWidth 
          onClick={() => {
            startNewChat();
            if (onMobileClose) onMobileClose();
          }}
          color="primary"
        >
          New chat
        </Button>
      </Box>
      
      {/* Conversations List */}
      <Box sx={{ overflowY: 'auto', flex: 1, py: 1 }}>
        {conversations.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No conversations yet
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Start a new chat to begin
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {conversations.map((c) => renderConversationItem(c))}
          </List>
        )}
      </Box>
    </>
  );

  return (
    <>
      {/* Desktop - Fixed Sidebar */}
      <Box
        sx={{
          width: 300,
          height: 'calc(100vh - var(--nav-height))', // below the sticky app navbar
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: 'var(--color-bg-primary)',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          position: 'fixed',
          left: 0,
          top: 'var(--nav-height)',
          zIndex: 1000,
          transition: 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)',
          transform: visible ? 'translateX(0)' : 'translateX(-100%)'
        }}
      >
        {panelContent}
      </Box>

      {/* Mobile - Drawer */}
      <Drawer
        anchor="left"
        open={mobileOpen || false}
        onClose={onMobileClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 300,
            bgcolor: 'var(--color-bg-primary)'
          }
        }}
      >
        {panelContent}
      </Drawer>

      {/* Rename Dialog */}
      <Dialog open={renaming.open} onClose={() => setRenaming({ open: false, title: '', sessionId: null })}>
        <DialogTitle>Rename conversation</DialogTitle>
        <DialogContent>
          <TextField 
            fullWidth 
            label="Title" 
            value={renaming.title} 
            onChange={(e) => setRenaming({ ...renaming, title: e.target.value })}
            autoFocus
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming({ open: false, title: '', sessionId: null })}>
            Cancel
          </Button>
          <Button variant="contained" onClick={applyRename}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, sessionId: null, title: '' })}>
        <DialogTitle>Delete conversation</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete "{confirmDelete.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, sessionId: null, title: '' })}>
            Cancel
          </Button>
          <Button 
            color="error" 
            variant="contained" 
            onClick={() => handleDelete(confirmDelete.sessionId)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HistoryPanel;
